// Models for the paid certificate on vitrin courses, issued by Amoozim or by a
// partner university. See docs/platform-certificate-frontend-guide (8).md.

/**
 * Which certificate a course sells. The only behavioural difference is whether
 * an exam stands in the way — read `requires_exam` for that, never this number.
 *
 * 1 گواهی دوره آموزیم (Amoozim) · 2 گواهی شرکت در دوره (university) ·
 * 3 گواهی پایان دوره (university, exam).
 */
export type CertificateType = 1 | 2 | 3 | (number & {});

/** Issued by Amoozim itself; every other type is issued by the university. */
export const AMOOZIM_CERTIFICATE_TYPE = 1;

/**
 * What the learner can do next. The server owns these rules — every screen is
 * built from this value, never re-derived on the client.
 */
export type CertificateNextAction =
  /** Not enrolled in the course — send them to buy it; registration is refused. */
  | "enroll"
  | "register"
  | "start_exam"
  | "resume_exam"
  | "pay_retry"
  | "pay_issuance"
  /** Paid; an admin is checking the identity. A waiting state, never a pay button. */
  | "awaiting_review"
  /** Approved; the document is being produced. A waiting state. */
  | "awaiting_issue"
  | "view_certificate"
  /** Identity refused — show `rejection_reason` and let them register again. */
  | "rejected"
  /** Withdrawn after issue. Nothing to do. */
  | "revoked"
  | "unavailable";

/** The body named on the document — read it, never hardcode a university. */
export interface CertificateAuthority {
  /** Full name, e.g. «وزارت علوم، تحقیقات و فناوری — دانشگاه صنعتی قم». */
  title: string;
  /** For tight spots, e.g. «دانشگاه صنعتی قم». */
  short_title: string;
}

/** Informational fee parts, toman. `price` on a delivery mode is what is charged. */
export interface CertificateFees {
  digital: number | null;
  physical: number | null;
  shipping: number | null;
}

/**
 * One way to receive the certificate. `price` is already the total for this
 * choice — never sum `fees` client-side.
 */
export interface CertificateDeliveryMode {
  /** 1 digital · 2 printed & posted · 3 both. Sent back as `delivery_mode`. */
  value: number;
  label: string;
  /** Toman. */
  price: number;
  /** Address is required when true and rejected by the API when false. */
  requires_address: boolean;
}

export interface CertificateOffering {
  id: number;
  title: string;
  description?: string | null;
  type: CertificateType;
  type_label: string;
  authority?: CertificateAuthority | null;
  /** Toman. `null` on the exam-free types — there is nothing to retry. */
  retry_price: number | null;
  fees: CertificateFees;
  /** The only list of delivery options to show. */
  delivery_modes: CertificateDeliveryMode[];
  requires_exam: boolean;
  has_exam: boolean;
}

/**
 * One certificate as published on the course detail, for buyers who can't
 * reach the status endpoint yet. A course may sell several (at most one per
 * type), so the detail carries a list. Absent on listings.
 */
export interface PlatformCertificateOffer {
  /** The id every write endpoint is nested under — carried into the certificate screen. */
  offering_id: number;
  available: boolean;
  title: string;
  description?: string | null;
  type: CertificateType;
  type_label: string;
  /** Served on the course detail too, though the guide documents it on status only. */
  authority?: CertificateAuthority | null;
  /**
   * Toman. The cheapest delivery option — a "from" price, since the learner
   * has not chosen how they want it yet. Render it as «از … تومان».
   */
  issuance_price_from: number;
  /** Toman. Only ever shown beside `first_attempt_free`; `null` without an exam. */
  retry_price: number | null;
  first_attempt_free: boolean;
  requires_exam: boolean;
  /**
   * The blank certificate design — what the learner would actually receive,
   * with no learner data on it. A plain public URL, unlike the signed
   * `download_url` of an issued certificate, so it is safe to cache.
   * `null` until an admin uploads one; an offering sells fine without it.
   */
  template_image_url?: string | null;
  /** Generated preview of the same design, for smaller surfaces. */
  template_thumb_url?: string | null;
}

/**
 * Where an application stands. `next_action` is derived from this server-side,
 * so the screen is driven by that — `status_label` is only a caption.
 *
 * 10 registered · 20 awaiting exam · 30 awaiting payment · 40 awaiting review ·
 * 50 approved · 60 issued · 90 rejected · 95 revoked.
 */
export type CertificateApplicationStatus =
  | 10
  | 20
  | 30
  | 40
  | 50
  | 60
  | 90
  | 95
  | (number & {});

export interface CertificateApplication {
  id: number;
  status: CertificateApplicationStatus;
  status_label: string;
  /** Written for the learner — display as-is. Set only when rejected. */
  rejection_reason: string | null;
  full_name: string;
  father_name: string;
  national_code: string;
  /** Gregorian YYYY-MM-DD. */
  birthdate: string;
  applied_at: string;
}

/** Suggested registration values from the learner's account (pre-registration only). */
export interface CertificatePrefill {
  first_name: string | null;
  last_name: string | null;
  /** Always null — no account holds one. */
  father_name: string | null;
  national_code: string | null;
  birthdate: string | null;
}

export interface CertificateExamState {
  attempts_used: number;
  has_passed: boolean;
  active_attempt_id: number | null;
  can_start: boolean;
  next_sitting_is_free: boolean;
  /** True when buying a retry is what unblocks `can_start`. */
  requires_payment: boolean;
  /** Ready-to-display Persian sentence whenever `can_start` is false. */
  reason: string | null;
}

/** 0 pending · 1 printed · 2 shipped · 3 delivered · 4 returned. */
export type CertificateFulfillmentStatus = 0 | 1 | 2 | 3 | 4;

/** The address a parcel was actually sent to, read off the order that paid for it. */
export interface CertificateDelivery {
  recipient_name: string;
  recipient_phone: string | null;
  address_line: string;
  postal_code: string;
  state_id: number | null;
  city_id: number | null;
}

export interface IssuedPlatformCertificate {
  id: number;
  status: number;
  /**
   * The verification handle. Only ever used inside the QR / verification link —
   * never shown as a public identifier.
   */
  certificate_code: string | null;
  /** The issuing body's sequential serial, printed on the document. */
  issuance_code: string | null;
  /** The issuing body's licence number, printed on the document. */
  registration_number: string | null;
  verification_url: string | null;
  /** Ready-to-render SVG data URI encoding `verification_url`. */
  verification_qr: string | null;
  published_at: string | null;
  /** What the learner bought — one of `offering.delivery_modes`. */
  delivery_mode: number;
  delivery_mode_label: string;
  /**
   * Whether a downloadable copy was bought at all. Branch on this, not on
   * `download_url` being null — null also means "still rendering".
   */
  has_digital_copy: boolean;
  /**
   * Signed link that expires in 24h. Never cache or persist it — re-fetch the
   * status endpoint whenever the learner wants the file. Always `null` for a
   * print-only certificate; otherwise `null` until the document is rendered.
   */
  download_url: string | null;
  /** `null` for a digital-only certificate — there is no parcel. */
  fulfillment_status: CertificateFulfillmentStatus | null;
  /** Persian label — display this rather than mapping the integer. */
  fulfillment_status_label: string | null;
  carrier: string | null;
  /** The courier's own code. Arrives some time after `shipped_at`. */
  tracking_code: string | null;
  shipped_at: string | null;
  delivered_at: string | null;
  /** Set when the parcel came back; the learner can pay to have it posted again. */
  returned_at: string | null;
  /** A re-posting is bought and not yet dispatched — a second one is refused. */
  resend_pending: boolean;
  /** Where it was posted; the resend form opens on this. `null` if never posted. */
  delivery: CertificateDelivery | null;
}

/**
 * Issuing is a manual admin step, so a learner can have paid and still hold no
 * certificate. This block covers that window.
 */
export interface CertificateIssuanceState {
  paid: boolean;
  order_reference: string | null;
  paid_at: string | null;
  awaiting_issue: boolean;
}

/**
 * One certificate on the course — an independent journey with its own
 * application, exam, payments and `next_action`.
 */
export interface PlatformCertificateEntry {
  /** `offering.id` is what every write endpoint is nested under. */
  offering: CertificateOffering;
  registered: boolean;
  application: CertificateApplication | null;
  prefill: CertificatePrefill | null;
  /** `null` for the exam-free types. */
  exam: CertificateExamState | null;
  issuance: CertificateIssuanceState | null;
  certificate: IssuedPlatformCertificate | null;
  next_action: CertificateNextAction;
}

/** `GET .../platform-certificate` — one entry per certificate the course sells. */
export interface PlatformCertificateStatus {
  /** The one mini-app-side requirement — the same answer for every certificate. */
  enrolled: boolean;
  /** Cheapest first. Render one card per entry; never assume the first is "the" one. */
  certificates: PlatformCertificateEntry[];
}

// --- Registration -----------------------------------------------------------

export interface RegisterCertificateBody {
  first_name: string;
  last_name: string;
  father_name: string;
  /** Exactly 10 digits, always a string (leading zeros). */
  national_code: string;
  /** Gregorian YYYY-MM-DD, before today. */
  birthdate: string;
}

export interface RegisterCertificateResponse {
  application_id: number;
}

// --- Orders -----------------------------------------------------------------

export enum CertificateOrderKind {
  Retry = 1,
  Issuance = 2,
  /** Post a returned parcel again — only while `fulfillment_status` is 4. */
  ResendShipping = 3,
}

export interface CertificateDeliveryDetails {
  recipient_name: string;
  address_line: string;
  /** Exactly 10 digits, always a string. */
  postal_code: string;
  recipient_phone?: string;
  state_id?: number;
  /** What the carrier addresses the parcel by — collect it whenever posting. */
  city_id?: number;
}

/**
 * The API rejects fields that don't belong to the order: a retry carries
 * nothing, a digital-only issuance carries no address, and `delivery_mode` is
 * sent with an issuance only.
 */
export type CreateCertificateOrderInput =
  | { kind: CertificateOrderKind.Retry }
  | { kind: CertificateOrderKind.Issuance; delivery_mode: number }
  | ({ kind: CertificateOrderKind.Issuance; delivery_mode: number } & CertificateDeliveryDetails)
  | ({ kind: CertificateOrderKind.ResendShipping } & CertificateDeliveryDetails);

/**
 * `source` mirrors what `POST /courses/{course}/buy` is given and decides which
 * frontend the gateway returns to. This app is the vitrin storefront, so it is
 * always `1`; omitting it defaults to the mini-app page server-side, which
 * strands the buyer on another app's domain. The hook always adds it.
 */
export type CreateCertificateOrderBody = CreateCertificateOrderInput & { source: 1 };

export interface CertificateOrderResponse {
  order_id: number;
  reference_key: string;
  authority: string;
  /** What will actually be charged, tax included. */
  amount: number;
  /** JSON string holding `{ action, inputs, method }`. */
  form: string;
}

/**
 * Decoded `payload` on the post-gateway result page. A certificate order returns
 * to the SAME page a course purchase does, so this carries the course purchase
 * keys plus certificate-only extras. A display hint only — the next step always
 * comes from a fresh status fetch.
 */
export interface CertificatePaymentPayload {
  /** Absent on a course purchase; this is how the shared page tells the two apart. */
  kind: "certificate";
  /**
   * The same strings a course purchase sends — not `PAID`/`FAILED`. The guide
   * documents only these two, but the string stays open: a course purchase can
   * also come back `"Pending"` (SnappPay), and telling a payer their payment
   * failed because we didn't recognise a status is the one unacceptable outcome.
   */
  status: "Completed" | "Failed" | (string & {});
  /** The order reference (`CRT-…`), in the slot a course purchase uses for its factor. */
  factor_id: string;
  /** Same value as `factor_id`. */
  order_reference: string;
  order_kind: CertificateOrderKind;
  /** Persian label for the fee that was paid — display this, don't map the integer. */
  order_kind_label: string;
  /**
   * Which certificate was paid for — a course may sell two. Optional only
   * because servers older than guide v8 don't send it.
   */
  offering_id?: number;
  offering_title?: string;
  offering_type?: CertificateType;
  course_id: number;
  course_name?: string;
  mini_app_uuid: string;
  created_at: string;
  purchase_source?: number;
  /** Toman, tax included. Absent on failure. */
  price?: number;
  /** Absent on failure. */
  paid_at?: string;
  /** From the bank response, on failure. */
  reason?: string;
}

// --- Exam -------------------------------------------------------------------

/** 0 single choice · 1 multiple choice. */
export enum CertificateQuestionType {
  SingleChoice = 0,
  MultipleChoice = 1,
}

/** Options never carry `is_correct` on this exam. */
export interface CertificateQuestionOption {
  id: number;
  question_id: number;
  text: string;
  sort_order: number;
}

export interface CertificateQuestion {
  id: number;
  mini_app_uuid: string | null;
  text: string;
  type: CertificateQuestionType | number;
  difficulty?: number;
  sort_order: number;
  options: CertificateQuestionOption[];
}

export interface CertificateExamAttempt {
  id: number;
  quiz_id: number;
  user_id: number;
  start_time: string;
  end_time: string | null;
  score: number | null;
  passed: boolean | null;
  status: number;
  status_label: string;
  questions: CertificateQuestion[];
  answers?: {
    question_id: number;
    question_option_id: number | null;
    answer_text: string | null;
  }[];
  quiz: {
    id: number;
    title: string;
    /** Minutes; null when untimed. */
    time_limit: number | null;
    pass_mark: number;
  };
}

/** Returned by exam/start and exam/current-attempt. */
export interface CertificateAttemptResponse {
  attempt: CertificateExamAttempt;
  /** Seconds left, or null when the exam has no time limit. */
  remaining_time: number | null;
}

export interface SubmitCertificateAnswerBody {
  question_id: number;
  question_option_id?: number | null;
  answer_text?: string | null;
}

export interface FinishCertificateExamResponse {
  attempt_id: number;
  /** Percentage of the whole paper. */
  score: number;
  passed: boolean;
  pass_mark: number;
  next_action: CertificateNextAction;
}

// --- Public verification ----------------------------------------------------

/**
 * `GET /certificates/verify/{code}`. An unknown code is a 200 with
 * `found: false` and every other detail `null`, so a hit and a miss render
 * from one template. Holds nothing beyond what is printed on the certificate.
 */
export interface CertificateVerification {
  found: boolean;
  code: string;
  issuance_code: string | null;
  registration_number: string | null;
  /** 1 Amoozim or a partner university · 0 the mini-app. */
  issuer: 0 | 1 | null;
  issuer_label: string | null;
  /** The body named on the document; `null` for a mini-app certificate. */
  authority: string | null;
  holder_name: string | null;
  course_title: string | null;
  /** YYYY-MM-DD. */
  issued_on: string | null;
  is_valid: boolean;
  revoked: boolean;
  verification_url: string | null;
  /**
   * Whether the issued document itself can be shown to the verifier. Not in
   * the guide yet — served by the backend since 2026-09-24, so optional.
   */
  has_document?: boolean;
  /** Signed link to the PDF; expires within hours, so only ever used straight from the response. */
  document_url?: string | null;
}
